import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Shield, ShieldCheck, ShieldAlert, ShieldX, AlertTriangle, 
  Loader2, ChevronDown, ChevronRight, FileCode, Clock, 
  Scan, CheckCircle2, XCircle, Info, ExternalLink,
  Lock, Key, Database, Bug
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityConfig = {
  CRITICAL: { color: "bg-red-600 text-white", icon: ShieldX, textColor: "text-red-400" },
  HIGH: { color: "bg-orange-600 text-white", icon: ShieldAlert, textColor: "text-orange-400" },
  MEDIUM: { color: "bg-yellow-600 text-black", icon: AlertTriangle, textColor: "text-yellow-400" },
  LOW: { color: "bg-blue-600 text-white", icon: Info, textColor: "text-blue-400" },
  INFO: { color: "bg-gray-600 text-white", icon: Info, textColor: "text-gray-400" }
};

const toolIcons = {
  "Checkov": Shield,
  "tfsec": Lock,
  "Trivy": Bug,
  "GitLeaks-patterns": Key
};

const FindingItem = ({ finding, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  const severity = finding.severity?.toUpperCase() || "MEDIUM";
  const config = severityConfig[severity] || severityConfig.MEDIUM;
  const SeverityIcon = config.icon;
  const ToolIcon = toolIcons[finding.tool] || Shield;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger 
        className="w-full"
        data-testid={`finding-${index}`}
      >
        <div className="flex items-center gap-3 p-3 hover:bg-zinc-800/50 rounded-lg transition-colors cursor-pointer border border-zinc-800 mb-2">
          <div className="flex items-center gap-2">
            <Badge className={config.color}>
              <SeverityIcon className="w-3 h-3 mr-1" />
              {severity}
            </Badge>
          </div>
          
          <div className="flex-1 text-left">
            <div className="flex items-center gap-2">
              <ToolIcon className="w-4 h-4 text-zinc-500" />
              <span className="text-sm text-zinc-400">{finding.tool}</span>
              <span className="text-xs text-zinc-600">|</span>
              <span className="text-xs font-mono text-zinc-500">{finding.rule_id}</span>
            </div>
            <p className="text-sm text-zinc-200 mt-1">{finding.title}</p>
          </div>
          
          <div className="flex items-center gap-2 text-zinc-500">
            <FileCode className="w-4 h-4" />
            <span className="text-xs font-mono">{finding.file_path}</span>
            {finding.line_start && (
              <span className="text-xs">L{finding.line_start}</span>
            )}
            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>
      </CollapsibleTrigger>
      
      <CollapsibleContent>
        <div className="ml-4 mb-4 p-4 bg-zinc-900 rounded-lg border border-zinc-800">
          <div className="space-y-3">
            <div>
              <h5 className="text-xs font-medium text-zinc-500 uppercase mb-1">Description</h5>
              <p className="text-sm text-zinc-300">{finding.description}</p>
            </div>
            
            {finding.resource && (
              <div>
                <h5 className="text-xs font-medium text-zinc-500 uppercase mb-1">Resource</h5>
                <code className="text-xs bg-zinc-800 px-2 py-1 rounded text-cyan-400">
                  {finding.resource}
                </code>
              </div>
            )}
            
            {finding.remediation && (
              <div>
                <h5 className="text-xs font-medium text-zinc-500 uppercase mb-1">Remediation</h5>
                <p className="text-sm text-emerald-400">{finding.remediation}</p>
              </div>
            )}
            
            {finding.reference_url && (
              <a 
                href={finding.reference_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Learn more
              </a>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const SummaryCard = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center gap-2 p-3 bg-zinc-900 rounded-lg border border-zinc-800">
    <Icon className={`w-5 h-5 ${color}`} />
    <div>
      <div className="text-lg font-bold text-white">{value}</div>
      <div className="text-xs text-zinc-500">{label}</div>
    </div>
  </div>
);

export const IaCSecurityScanner = ({ project, onScanComplete }) => {
  const { token, hasAccess } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(project?.iac_scan_results || null);
  const [toolsStatus, setToolsStatus] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterTool, setFilterTool] = useState("all");

  const fetchToolsStatus = async () => {
    try {
      const response = await axios.get(`${API}/ai/iac/tools/status`);
      setToolsStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch tools status:", error);
    }
  };

  const runSecurityScan = async () => {
    if (!hasAccess('growth')) {
      toast.error("IaC security scanning requires Growth or Enterprise subscription");
      return;
    }

    if (!project?.phase4?.code_files || Object.keys(project.phase4.code_files).length === 0) {
      toast.error("No IaC code found. Please generate IaC first.");
      return;
    }

    try {
      setScanning(true);
      toast.info("Running security scan... This may take a minute.");
      
      const response = await axios.post(
        `${API}/ai/iac/scan`,
        { project_id: project.id },
        { 
          timeout: 180000,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      
      setScanResults(response.data);
      
      const summary = response.data.summary;
      if (summary.critical > 0) {
        toast.error(`Scan complete: Found ${summary.critical} critical issues!`);
      } else if (summary.high > 0) {
        toast.warning(`Scan complete: Found ${summary.high} high severity issues.`);
      } else if (summary.total_findings > 0) {
        toast.info(`Scan complete: Found ${summary.total_findings} findings.`);
      } else {
        toast.success("Scan complete: No security issues found!");
      }
      
      if (onScanComplete) {
        onScanComplete(response.data);
      }
    } catch (error) {
      console.error("Scan failed:", error);
      toast.error(error.response?.data?.detail || "Security scan failed. Please try again.");
    } finally {
      setScanning(false);
    }
  };

  // Fetch tools status on mount
  useState(() => {
    fetchToolsStatus();
  }, []);

  const filteredFindings = scanResults?.findings?.filter(finding => {
    if (filterSeverity !== "all" && finding.severity?.toUpperCase() !== filterSeverity) {
      return false;
    }
    if (filterTool !== "all" && finding.tool !== filterTool) {
      return false;
    }
    return true;
  }) || [];

  const uniqueTools = [...new Set(scanResults?.findings?.map(f => f.tool) || [])];

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="iac-security-scanner">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600">
              <Scan className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">IaC Security Scanner</CardTitle>
              <CardDescription className="text-zinc-400">
                Scan your infrastructure code for security vulnerabilities
              </CardDescription>
            </div>
          </div>
          
          <Button
            onClick={runSecurityScan}
            disabled={scanning || !project?.phase4?.code_files}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            data-testid="run-security-scan-btn"
          >
            {scanning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Run Security Scan
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Tools Status */}
        {toolsStatus && (
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <h4 className="text-sm font-medium text-zinc-300 mb-2">Available Scanners</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(toolsStatus).map(([tool, info]) => (
                <Badge 
                  key={tool}
                  variant={info.available ? "default" : "secondary"}
                  className={info.available ? "bg-emerald-600/20 text-emerald-400 border-emerald-600/30" : "bg-zinc-700 text-zinc-500"}
                >
                  {info.available ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                  {tool === "gitleaks_patterns" ? "Secret Detection" : tool.charAt(0).toUpperCase() + tool.slice(1)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* No IaC Code Message */}
        {!project?.phase4?.code_files && (
          <div className="p-6 text-center bg-zinc-800/50 rounded-lg border border-zinc-700">
            <FileCode className="w-12 h-12 mx-auto text-zinc-500 mb-3" />
            <p className="text-zinc-400">No IaC code generated yet.</p>
            <p className="text-sm text-zinc-500 mt-1">Generate IaC using the export feature to run security scans.</p>
          </div>
        )}

        {/* Scan Results */}
        {scanResults && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <SummaryCard 
                icon={Shield} 
                label="Total Findings" 
                value={scanResults.summary?.total_findings || 0}
                color="text-zinc-400"
              />
              <SummaryCard 
                icon={ShieldX} 
                label="Critical" 
                value={scanResults.summary?.critical || 0}
                color="text-red-500"
              />
              <SummaryCard 
                icon={ShieldAlert} 
                label="High" 
                value={scanResults.summary?.high || 0}
                color="text-orange-500"
              />
              <SummaryCard 
                icon={AlertTriangle} 
                label="Medium" 
                value={scanResults.summary?.medium || 0}
                color="text-yellow-500"
              />
              <SummaryCard 
                icon={Info} 
                label="Low" 
                value={scanResults.summary?.low || 0}
                color="text-blue-500"
              />
              <SummaryCard 
                icon={Key} 
                label="Secrets" 
                value={scanResults.summary?.secrets_detected || 0}
                color="text-purple-500"
              />
            </div>

            {/* Scan Metadata */}
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Scanned in {scanResults.scan_duration_seconds?.toFixed(2)}s
              </div>
              <div className="flex items-center gap-1">
                <Database className="w-3 h-3" />
                {scanResults.tools_executed?.length || 0} tools used
              </div>
              <div className="flex items-center gap-1">
                <FileCode className="w-3 h-3" />
                {Object.keys(project?.phase4?.code_files || {}).length} files scanned
              </div>
            </div>

            {/* Filters */}
            {scanResults.findings?.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">Severity:</span>
                  <select
                    value={filterSeverity}
                    onChange={(e) => setFilterSeverity(e.target.value)}
                    className="bg-zinc-800 text-zinc-300 text-sm px-2 py-1 rounded border border-zinc-700"
                    data-testid="severity-filter"
                  >
                    <option value="all">All</option>
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">Tool:</span>
                  <select
                    value={filterTool}
                    onChange={(e) => setFilterTool(e.target.value)}
                    className="bg-zinc-800 text-zinc-300 text-sm px-2 py-1 rounded border border-zinc-700"
                    data-testid="tool-filter"
                  >
                    <option value="all">All Tools</option>
                    {uniqueTools.map(tool => (
                      <option key={tool} value={tool}>{tool}</option>
                    ))}
                  </select>
                </div>
                <span className="text-xs text-zinc-500 ml-auto">
                  Showing {filteredFindings.length} of {scanResults.findings?.length || 0} findings
                </span>
              </div>
            )}

            {/* Findings List */}
            {scanResults.findings?.length > 0 ? (
              <ScrollArea className="h-[400px]">
                <div className="space-y-1 pr-4">
                  {filteredFindings.map((finding, index) => (
                    <FindingItem key={index} finding={finding} index={index} />
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="p-8 text-center bg-emerald-900/20 rounded-lg border border-emerald-600/30">
                <ShieldCheck className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                <h3 className="text-xl font-bold text-emerald-400 mb-2">All Clear!</h3>
                <p className="text-zinc-400">No security issues found in your infrastructure code.</p>
              </div>
            )}

            {/* Errors */}
            {scanResults.errors?.length > 0 && (
              <div className="p-3 bg-red-900/20 rounded-lg border border-red-600/30">
                <h4 className="text-sm font-medium text-red-400 mb-2">Scan Warnings</h4>
                <ul className="text-xs text-red-300 space-y-1">
                  {scanResults.errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default IaCSecurityScanner;
